package nl.volleyplanner.api.auth

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.AuthTokenResult
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import nl.volleyplanner.api.AnonymousPageTokenResult
import nl.volleyplanner.api.User
import nl.volleyplanner.web.CoroutineManager

enum class AuthLevel {
    /**
     * Public access: has no anonymous token for an organisation
     */
    AnonymousPublic,

    /**
     * Anonymous user for an organisation. Token not yet verified
     */
    BasicUserNotVerified,

    /**
     * Anonymous user access to an organisation by token
     */
    BasicUser,

    /**
     * Registered users for an organisation
     */
    AdminUser
}


data class AuthContext(
    val firebaseUser: FirebaseUser?,
    val authTokenResult: AuthTokenResult?,
    @Deprecated("Should not be part of firebase auth context")
    val dbUser: User?
) {
    fun isReadyForAction() = firebaseUser != null && authTokenResult != null && authTokenResult.token != null

    fun userEquals(other: AuthContext?) = firebaseUser?.uid == other?.firebaseUser?.uid
    fun authTokenEquals(other: AuthContext?) = authTokenResult?.token == other?.authTokenResult?.token
}

data class AuthUser(
    val username: String,
    val authLevel: AuthLevel,
    val email: String?,
    val dbUser: User?
) {
    companion object {
        private val READY_LEVELS = setOf(AuthLevel.BasicUser, AuthLevel.AdminUser)
        val ANONYMOUS_USER = AuthUser("Anoniem", AuthLevel.AnonymousPublic, null, null)
    }
}


object AuthContextService {
    private val _authContextState = MutableStateFlow<AuthContext>(AuthContext(null, null, null))

    private val _authUserState = MutableStateFlow<AuthUser?>(null)
    val authUserState = _authUserState.asStateFlow()

    private val _apiTokenState = MutableStateFlow<String?>(null)
    val apiTokenState = _apiTokenState.asStateFlow()

    /**
     * The current user
     * Null if authentication is still initializing (i.e. the first user, including anonymous, still needs to be resolved)
     */
    val authUser: AuthUser?
        get() = _authUserState.value

    private var _anonymousToken: String? = null
    val anonymousToken: String?
        get() = _anonymousToken

    private var anonymousVerified: AnonymousPageTokenResult? = null
    val anonymousPageContext: AnonymousPageTokenResult?
        get() = anonymousVerified

    fun setAnonymousVerified(verified: AnonymousPageTokenResult?) {
        if (anonymousVerified != verified) {
            anonymousVerified = verified
            updateAuthUser()
        }
    }

    fun setAnonymousToken(token: String) {
        if (anonymousToken != token) {
            _anonymousToken = token
            anonymousVerified = null
            updateAuthUser()
        }
    }

    fun setDbUser(user: User) {
        _authContextState.value = _authContextState.value.copy(dbUser = user)
    }

    suspend fun refreshApiToken() {
        val apiToken = _authContextState.value.firebaseUser?.getIdTokenResult(true)?.token
        _apiTokenState.value = apiToken
    }

    private fun updateAuthUser() {
        val authContext = _authContextState.value
        val authUser = if (authContext.firebaseUser != null) {
            // Currently if logged into Firebase the user is Admin as we have no differentiation
            // Basic user is if there is an anonymous token available
            _apiTokenState.value = authContext.authTokenResult?.token
            AuthUser(authContext.firebaseUser.uid, AuthLevel.AdminUser, authContext.firebaseUser.email!!, authContext.dbUser)
        } else {
            if (anonymousToken != null) {
                if (anonymousVerified != null) {
                    AuthUser("anoniem", AuthLevel.BasicUser, null, null)
                } else {
                    AuthUser("anoniem", AuthLevel.BasicUserNotVerified, null, null)
                }
            } else {
                AuthUser("anoniem", AuthLevel.AnonymousPublic, null, null)
            }
        }
        _authUserState.value = authUser
    }

    fun launchService() {
        CoroutineManager.newServiceScope("AuthContextService").launch {
            Firebase.auth.authStateChanged
                .combine(Firebase.auth.idTokenChanged, { auth, idToken -> Pair(auth, idToken)})
                .stateIn(CoroutineScope(Dispatchers.Default), SharingStarted.Companion.Eagerly, Pair(Firebase.auth.currentUser, Firebase.auth.currentUser))
                .collect({ (authStateUser, idTokenUser) ->
                    val dbUser = if (authStateUser?.uid != _authContextState.value.firebaseUser?.uid) {
                        null
                    } else _authContextState.value.dbUser
                    val apiToken = idTokenUser?.getIdTokenResult(true)
                    _authContextState.value = _authContextState.value.copy(
                        firebaseUser = authStateUser,
                        authTokenResult = apiToken,
                        dbUser = dbUser
                    )
                    updateAuthUser()
                })
        }
    }
}